import React, { useRef } from 'react';
import '../../App.css';
import './session-status.css';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Info, Monitor, TriangleAlert, Upload } from 'lucide-react';
import { ComputeServiceType, JobType } from '../../utils/types';
import SiteIcon from '../../tools/site-icon';
import Scrollbox from '../../tools/scrollbox';
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { addTab } from './interactive-sessions-slice';
import PleaseWait from '../../tools/please-wait';
import { JobList, JobDetails } from '../data-management/job-status';

//  --------------------------------------------------------------------------
//
//  H E L P E R S
//
//  --------------------------------------------------------------------------

const STAGING_STATUSES = ['STAGING', 'RETRYING', 'UNCHECKED', 'PREPARING'];
const FAILED_STATUSES = ['FAILED', 'ERROR', 'REVOKED', 'STOPPED'];
const COMPLETED_STATUSES = ['COMPLETED', 'COMPLETE'];

//  --------------------------------------------------------------------------
//
//  C O M P O N E N T
//
//  --------------------------------------------------------------------------

export default function SessionStatus(props: { computeServiceId: string }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );
  const session: ComputeServiceType | undefined = sSessions.find(
    (item) => item.compute_service_id === props.computeServiceId,
  );

  // file-browser state variables.
  const sAllJobs: JobType[] = useAppSelector(
    (state) => state.fileBrowser.dataManagementJobs,
  );
  const sDisplayedStagingJob: JobType | undefined = useAppSelector(
    (state) => state.fileBrowser.displayedStagingJob,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // logsFocusRow ref required by JobList and JobDetails.
  const logsFocusRow = useRef<string | undefined>(undefined);

  if (session === undefined) {
    return (
      <div
        className="fc text-colour"
        style={{ flex: '1 1', height: '100%', width: '100%', overflow: 'hidden' }}
      >
        <PleaseWait text="Loading session....." visible={true} />
      </div>
    );
  }

  //  --------------------------------------------------------------------------
  //
  //  D E R I V E D   V A L U E S
  //
  //  --------------------------------------------------------------------------

  // the SKAHA session ID is stored in compute_service_id for CANFAR sessions.
  const linkedJobs: JobType[] = sAllJobs.filter(
    (job) => job.skaha_session_id === props.computeServiceId,
  );

  const stagingJobs: JobType[] = linkedJobs.filter((job) =>
    STAGING_STATUSES.includes(job.status),
  );
  const failedJobs: JobType[] = linkedJobs.filter((job) =>
    FAILED_STATUSES.includes(job.status),
  );
  const completedJobs: JobType[] = linkedJobs.filter((job) =>
    COMPLETED_STATUSES.includes(job.status),
  );

  const totalFilesWaiting: number = stagingJobs.reduce(
    (sum, job) => sum + job.num_items,
    0,
  );
  const totalFilesFailed: number = failedJobs.reduce(
    (sum, job) => sum + job.num_items,
    0,
  );
  const totalFilesCompleted: number = completedJobs.reduce(
    (sum, job) => sum + job.num_items,
    0,
  );

  const sessionStatus: string = session.overridden_status ?? session.compute_status;
  const isSessionRunning: boolean =
    session.compute_status === 'running' && session.overridden_status !== 'removing';
  const isSessionPending: boolean = session.compute_status === 'pending';
  const isSessionFailed: boolean = ['error', 'failed'].includes(session.compute_status);

  // info message alongside the View session button when staging is incomplete or has failed,
  // but only once the session is running.
  const showStagingWarning: boolean =
    isSessionRunning && (stagingJobs.length > 0 || failedJobs.length > 0);

  // staging summary message, shown whenever there are linked jobs.
  const getStagingSummary = (): string => {
    if (stagingJobs.length > 0 && failedJobs.length > 0)
      return `Failed to stage ${totalFilesFailed} file(s). Currently waiting for ${totalFilesWaiting} file(s) to be staged by ${stagingJobs.length} job(s).`;
    if (stagingJobs.length > 0)
      return `Currently waiting for ${totalFilesWaiting} file(s) to be staged by ${stagingJobs.length} job(s).`;
    if (failedJobs.length > 0) return `Failed to stage ${totalFilesFailed} file(s).`;
    return `${totalFilesCompleted} file(s) have been successfully staged.`;
  };
  const stagingSummary: string = getStagingSummary();

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  // only show job details if the selected job belongs to this session.
  const isLinkedJobSelected: boolean =
    sDisplayedStagingJob !== undefined &&
    sDisplayedStagingJob.skaha_session_id === props.computeServiceId;

  if (isLinkedJobSelected) {
    return <JobDetails logsFocusRow={logsFocusRow} mode="back" />;
  }

  return (
    <div
      className="fc text-colour session-status"
      style={{ flex: '1 1', overflow: 'hidden' }}
    >
      <Scrollbox direction="vertical" flex="1 1" width="100%" flexDirection="column">
        <div
          role="heading"
          aria-level={2}
          className="header-text"
          style={{ padding: '20px 20px 40px 20px' }}
        >
          Interactive session details:
        </div>

        <div
          className="fr session-status-row"
          style={{ alignItems: 'center', padding: '0px 20px 14px 20px', width: '100%' }}
        >
          <div className="session-status-icon" style={{ flex: '0 0 70px' }}>
            <Monitor width={30} height={30} strokeWidth={1.5} />
          </div>
          <span style={{ flex: '1 1', minWidth: 0 }}>
            {isSessionPending && (
              <div className="fr" style={{ gap: '10px' }}>
                <PleaseWait text="" small={true} visible={true} />
                Interactive session is currently being set up
              </div>
            )}
            {isSessionRunning && 'Interactive session is running'}
            {isSessionFailed && (
              <div
                className="fr text-colour"
                data-text-red="T"
                style={{ gap: '10px', alignItems: 'center' }}
              >
                <TriangleAlert width={30} height={30} strokeWidth={1.5} />
                There is an error with this interactive session
              </div>
            )}
            {!isSessionRunning &&
              !isSessionPending &&
              !isSessionFailed &&
              `Session status: ${sessionStatus}`}
          </span>
        </div>

        {session.site?.site && (
          <div
            className="fr session-status-site-row"
            style={{
              alignItems: 'center',
              padding: '0px 20px 14px 90px',
              gap: '10px',
              width: '100%',
            }}
          >
            <span className="session-status-site-label" style={{ marginRight: '80px' }}>
              {isSessionPending
                ? 'Setting up at site:'
                : isSessionRunning
                  ? 'Running at site:'
                  : 'Site:'}
            </span>
            <SiteIcon site={session.site} size={28} />
            <span>{session.site.site}</span>
          </div>
        )}

        {isSessionPending && (
          <div
            className="session-status-pending-hint"
            style={{ padding: '0px 20px 14px 90px', width: '100%', opacity: 0.75 }}
          >
            Your session will appear here when it is available
          </div>
        )}

        {linkedJobs.length > 0 && (
          <div
            className="fr session-status-row"
            style={{
              alignItems: 'center',
              padding: '30px 20px 14px 20px',
              width: '100%',
            }}
          >
            <div className="session-status-icon" style={{ flex: '0 0 70px' }}>
              <Upload width={30} height={30} strokeWidth={1.5} />
            </div>
            <span style={{ flex: '1 1', minWidth: 0 }}>
              <div className="fr" style={{ gap: '10px' }}>
                {stagingJobs.length > 0 && (
                  <PleaseWait text="" small={true} visible={true} />
                )}
                {stagingSummary}
              </div>
            </span>
          </div>
        )}

        {linkedJobs.length > 0 && (
          <div
            className="fr session-status-jobs-row"
            style={{ padding: '0px 20px 14px 20px', width: '100%' }}
          >
            <div className="session-status-icon" style={{ flex: '0 0 70px' }} />
            <div style={{ flex: '1 1', minWidth: 0 }}>
              <JobList
                logsFocusRow={logsFocusRow}
                standaloneComponent={false}
                canfarSessionId={session.compute_service_id}
              />
            </div>
          </div>
        )}

        {failedJobs.length > 0 && (
          <div
            className="fr text-colour session-status-jobs-row"
            data-text-red="T"
            style={{ padding: '0px 20px 14px 20px', width: '100%' }}
          >
            <div className="session-status-icon" style={{ flex: '0 0 70px' }} />
            <div
              className="fr"
              style={{ flex: '1 1', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}
            >
              <TriangleAlert width={30} height={30} strokeWidth={1.5} />
              <span style={{ flex: '1 1' }}>
                {failedJobs.length} job(s) have failed. Click on the info button to see
                error messages.
              </span>
            </div>
          </div>
        )}
        <div style={{ flex: '1 1' }} />
        <div
          className="fr session-status-footer"
          style={{ alignItems: 'center', padding: '14px 20px 14px 20px', width: '100%' }}
        >
          {showStagingWarning ? (
            <>
              <div className="session-status-icon" style={{ flex: '0 0 70px' }}>
                <Info width={30} height={30} strokeWidth={1.5} />
              </div>
              <span style={{ flex: '1 1' }}>
                You may not be able to access all of your data until staging is complete,
                but you may still view the session if you choose to
              </span>
            </>
          ) : (
            <div className="session-status-footer-spacer" style={{ flex: '1 1' }} />
          )}
          <div className="session-status-footer-action">
            <ToolButton
              name="view-session"
              onClick={() => dispatch(addTab({ service: session, infoDisplayed: false }))}
              tooltip={
                isSessionRunning
                  ? 'Open this interactive session'
                  : 'Session is not yet available'
              }
              text="View session"
              type={ToolButtonType.SECONDARY}
              disabled={!isSessionRunning}
            />
          </div>
        </div>
      </Scrollbox>
    </div>
  );
  //
} // SessionStatus
